import { Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Error403Unauthorized } from '@app/components';
import {
  FORBIDDEN_ERROR_MAIN_PART,
  ORGANIZATIONS_PATH,
  ORGANIZATIONS_TABLE_HEADER,
  PREFIX,
  U_KEY,
} from '@app/constants';
import { useAccessToOrganizations, usePagination } from '@app/hooks';
import { useSearchQuery } from '@app/services/rtkQuery/organization';
import { useErrorHandler } from '@app/shared/hooks';

import { adaptDataToTable } from '@pages/Organizations/OrganizationsList/utils';

import { useSecretLink } from '@pages/Organizations/OrganizationsList/hooks/useSecretLink';

import { OrganizationsFilterForm } from '@pages/Organizations/OrganizationsList/components/OrganizationsFilterForm';

import type { OrganizationsFilterFormValues } from '@pages/Organizations/OrganizationsList/Organizations.types';

import '@pages/Organizations/OrganizationsList/styles/Organizations.desktop.scss';
import '@pages/Organizations/OrganizationsList/styles/Organizations.mobile.scss';

export const Organizations = () => {
  const { areRightsToOrganizationsModule, hasUserRightToSecretLinks } =
    useAccessToOrganizations();

  const [total, setTotal] = useState(0);
  const [params, setParams] = useState<OrganizationsFilterFormValues>();

  const navigate = useNavigate();
  const { pageNumber, rowsPerPage, totalRowsCount, change } =
    usePagination(total);

  const { data, isLoading, error, isError } = useSearchQuery({
    params: {
      page: pageNumber,
      pageSize: rowsPerPage,
      ...params,
    },
  });

  useErrorHandler([{ flag: isError, error }]);

  const { openSelectModal, renderUserSelectModal, renderSecretLinkModal } =
    useSecretLink();

  const organizations = useMemo(() => {
    const handleNavigateToSettings = (id: number) => {
      navigate(`${ORGANIZATIONS_PATH}/settings/?id=${id}`);
    };

    const handleNavigateToUsers = (id: number) => {
      navigate(`${ORGANIZATIONS_PATH}/users/?id=${id}`);
    };

    return !data?.Data
      ? []
      : adaptDataToTable({
          data: data.Data!,
          hasUserRightToSecretLinks,
          handleNavigateToSettings,
          handleNavigateToUsers,
          openUserSelectModal: openSelectModal,
        });
  }, [data?.Data, hasUserRightToSecretLinks, navigate, openSelectModal]);

  useEffect(() => {
    setTotal(data?.Total || 0);
  }, [data]);

  if (!areRightsToOrganizationsModule || isError) {
    return <Error403Unauthorized title={`${FORBIDDEN_ERROR_MAIN_PART}.`} />;
  }

  return (
    <div className={`${PREFIX}organizations`}>
      <OrganizationsFilterForm onChange={setParams} />
      <div>
        <Table
          className="table"
          loading={isLoading}
          rowKey={(record) => String(record[U_KEY])}
          columns={ORGANIZATIONS_TABLE_HEADER}
          dataSource={organizations}
          rowClassName="organization-item"
          pagination={{
            size: 'default',
            showSizeChanger: true,
            total: totalRowsCount,
            pageSize: rowsPerPage,
            position: ['bottomLeft'],
            current: pageNumber,
            onChange: change,
            showTotal: (total, range) =>
              `Показаны ${range[0]}-${range[1]} из ${total}`,
          }}
        />
      </div>
      {renderUserSelectModal()}
      {renderSecretLinkModal()}
    </div>
  );
};
